import { some, sortBy, uniq, without } from "lodash-es";
import { useCallback, useMemo, useReducer } from "react";
import { i18nService } from "services";
import { IOption } from "types";
import { ITussendoortjesRow } from "./types";
import { renderDifficulty } from "./components/TussendoortjesTableRow";

interface IState {
  search?: string;
  thema?: string | null;
  difficulty?: string | null;
  sortColumn?: string | null;
  sortDirection?: string | null;
  favorites?: string[];
}

const initialState: IState = {
  search: "",
  thema: null,
  difficulty: null,
  sortColumn: "title",
  sortDirection: "asc",
  favorites: JSON.parse(
    localStorage.getItem("favorite-tussendoortjes-rows") || "[]"
  ),
};

enum Type {
  SET_SEARCH = "SET_SEARCH",
  SET_THEMA = "SET_THEMA",
  SET_DIFFICULTY = "SET_DIFFICULTY",
  SET_SORT_COLUMN = "SET_SORT_COLUMN",
  SET_SORT_DIRECTION = "SET_SORT_DIRECTION",
  ADD_TO_FAVORITES = "ADD_TO_FAVORITES",
  REMOVE_FROM_FAVORITES = "REMOVE_FROM_FAVORITES",
}

export const useTussendoortjes = ({ rows }: { rows: ITussendoortjesRow[] }) => {
  const [state, dispatch] = useReducer(
    (state: IState, { type, payload }: { type: Type; payload: any }) => {
      switch (type) {
        case Type.SET_SEARCH: {
          return {
            ...state,
            search: payload.search,
          };
        }

        case Type.SET_THEMA: {
          return {
            ...state,
            thema: payload.thema,
          };
        }

        case Type.SET_DIFFICULTY: {
          return {
            ...state,
            difficulty: payload.difficulty,
          };
        }

        case Type.SET_SORT_COLUMN: {
          return {
            ...state,
            sortColumn: payload.sortColumn,
          };
        }

        case Type.SET_SORT_DIRECTION: {
          return {
            ...state,
            sortDirection: payload.sortDirection,
          };
        }

        case Type.ADD_TO_FAVORITES: {
          localStorage.setItem(
            "favorite-tussendoortjes-rows",
            JSON.stringify([
              ...JSON.parse(
                localStorage.getItem("favorite-tussendoortjes-rows") || "[]"
              ),
              payload.favorite,
            ])
          );
          return {
            ...state,
            ...(state.favorites && {
              favorites: [...state.favorites, payload.favorite],
            }),
          };
        }

        case Type.REMOVE_FROM_FAVORITES: {
          localStorage.setItem(
            "favorite-tussendoortjes-rows",
            JSON.stringify(
              without(
                JSON.parse(
                  localStorage.getItem("favorite-tussendoortjes-rows") || "[]"
                ),
                payload.favorite
              )
            )
          );
          return {
            ...state,
            ...(state.favorites && {
              favorites: without(state.favorites, payload.favorite),
            }),
          };
        }

        default: {
          return state;
        }
      }
    },
    initialState
  );

  const setSearch = ({ search }: { search: string }) => {
    dispatch({
      type: Type.SET_SEARCH,
      payload: {
        search,
      },
    });
  };

  const setThema = (thema: string | null) => {
    dispatch({
      type: Type.SET_THEMA,
      payload: {
        thema,
      },
    });
  };

  const setDifficulty = (difficulty: string | null) => {
    dispatch({
      type: Type.SET_DIFFICULTY,
      payload: {
        difficulty,
      },
    });
  };

  const themaOptions = useMemo<IOption[]>(
    () => [
      {
        label: i18nService.copy["toolQuickSearch.choose"]?.replace(
          "@filter",
          "thema"
        ),
        value: "",
        type: "",
        name: "",
      },
      ...sortBy(
        uniq(
          rows
            .map(({ themes }) => themes)
            .flat()
            .filter((theme) => !!theme)
        ).map((theme: any) => ({
          label: theme,
          value: theme,
          name: theme,
          type: theme,
        })),
        "label"
      ),
    ],
    [rows]
  );

  const difficultyOptions = useMemo<IOption[]>(
    () => [
      {
        label: i18nService.copy["toolQuickSearch.choose"]?.replace(
          "@filter",
          "moeilijkheidsgraad"
        ),
        value: "",
        type: "",
        name: "",
      },
      ...sortBy(
        uniq(
          rows
            .map(({ difficulty }) => difficulty)
            .filter((type) => !!type)
            .flat()
        ).map((type: any) => ({
          label: renderDifficulty(type),
          value: type,
          name: type,
          type,
        })),
        "label"
      ),
    ],
    [rows]
  );

  const filteredRows = useMemo(() => {
    const filtered = rows.filter(
      ({
        title,
        description,
        themes,
        difficulty,
        goals,
        key_competencies,
        learning_areas,
      }) => {
        // If any field is not filled in, don't show the row.

        const match = (t: string, target?: string | null) =>
          !target || t.toLowerCase().includes(target.toLowerCase());

        const matchesSearch =
          match(title, state.search) ||
          (description && match(description, state.search));

        const matchesTheme = some(themes, (t: string) => match(t, state.thema));

        const matchesDifficulty = match(
          difficulty.toString(),
          state.difficulty
        );

        return matchesSearch && matchesTheme && matchesDifficulty;
      }
    );

    if (state.sortColumn === null || state.sortColumn === undefined)
      return filtered.sort((a, b) => {
        let aIsFavorite = state.favorites?.includes(a.title);
        let bIsFavorite = state.favorites?.includes(b.title);

        // If either A or B is a favorite, prioritize it
        if (aIsFavorite || bIsFavorite) {
          if (aIsFavorite && !bIsFavorite) {
            return -1;
          }
          if (!aIsFavorite && bIsFavorite) {
            return 1;
          }
        }

        // If either A or B is spotlight, prioritize it
        if (a.spotlight || b.spotlight) {
          if (a.spotlight && !b.spotlight) {
            return -1;
          }
          if (!a.spotlight && b.spotlight) {
            return 1;
          }
        }

        return 0;
      });

    return filtered.sort((a, b) => {
      //@ts-ignore
      let compareValueA = a[state.sortColumn].toString().trim();
      //@ts-ignore
      let compareValueB = b[state.sortColumn].toString().trim();

      if (state.sortColumn === "difficulty") {
        //@ts-ignore
        compareValueA = renderDifficulty(parseInt(a[state.sortColumn]));
        //@ts-ignore
        compareValueB = renderDifficulty(b[state.sortColumn]);
      }

      let aIsFavorite = state.favorites?.includes(a.title);
      let bIsFavorite = state.favorites?.includes(b.title);

      // If either A or B is a favorite, prioritize it
      if (aIsFavorite || bIsFavorite) {
        if (aIsFavorite && !bIsFavorite) {
          return -1;
        }
        if (!aIsFavorite && bIsFavorite) {
          return 1;
        }
      }

      // If either A or B is spotlight, prioritize it
      if (a.spotlight || b.spotlight) {
        if (a.spotlight && !b.spotlight) {
          return -1;
        }
        if (!a.spotlight && b.spotlight) {
          return 1;
        }
      }

      // If A and B are both favorites, or neither is a favorite, sort alphabetically
      if (typeof compareValueA === "string") {
        compareValueA = compareValueA.toLowerCase();
        compareValueB = compareValueB.toLowerCase();
      } else {
        compareValueA = compareValueA.map((val: string) => val.toLowerCase());
        compareValueB = compareValueB.map((val: string) => val.toLowerCase());
      }

      return state.sortDirection === "asc"
        ? compareValueA < compareValueB
          ? -1
          : 1
        : compareValueA > compareValueB
        ? -1
        : 1;
    });
    // eslint-disable-next-line
  }, [
    rows,
    state.difficulty,
    state.search,
    state.sortColumn,
    state.sortDirection,
    state.thema,
  ]);

  const handleSort = useCallback(
    (column: string) => {
      if (state.sortColumn === column) {
        if (state.sortDirection === "desc") {
          dispatch({
            type: Type.SET_SORT_COLUMN,
            payload: {
              sortColumn: null,
            },
          });
        } else {
          dispatch({
            type: Type.SET_SORT_DIRECTION,
            payload: {
              sortDirection: "desc",
            },
          });
        }
      } else {
        dispatch({
          type: Type.SET_SORT_COLUMN,
          payload: {
            sortColumn: column,
          },
        });

        dispatch({
          type: Type.SET_SORT_DIRECTION,
          payload: {
            sortDirection: "asc",
          },
        });
      }
    },
    [state.sortColumn, state.sortDirection]
  );

  const addToFavorites = (favorite: string) => {
    dispatch({
      type: Type.ADD_TO_FAVORITES,
      payload: {
        favorite,
      },
    });
  };

  const removeFromFavorites = (favorite: string) => {
    dispatch({
      type: Type.REMOVE_FROM_FAVORITES,
      payload: {
        favorite,
      },
    });
  };

  return {
    state: {
      sortColumn: state.sortColumn,
      sortDirection: state.sortDirection,
      favorites: state.favorites,
      themaOptions,
      difficultyOptions,
      filteredRows,
    },
    actions: {
      setSearch,
      setThema,
      setDifficulty,
      handleSort,
      addToFavorites,
      removeFromFavorites,
    },
  };
};
